import { supabase } from '../supabase';
import { checkRateLimit, resetRateLimit, type RateLimitResult } from './rateLimiter';
import { getClientIP } from './ipService';
import { isBlockedUserAgent, getUserAgentRiskScore } from './userAgentFilter';
import { analyzeInput, analyzePath, getInputRiskLevel } from './anomalyDetector';
import { verifyBrowser, hasValidVerification, startMouseTracking } from './browserVerification';

export interface SecurityCheckResult {
  allowed: boolean;
  requiresCaptcha: boolean;
  requiresDelay: boolean;
  delaySeconds: number;
  blockedReason?: string;
  riskScore: number;
  flags: string[];
}

export interface LoginSecurityContext {
  ipAddress: string;
  userAgent: string;
  email: string;
  honeypotTriggered: boolean;
  captchaVerified: boolean;
}

export async function performSecurityChecks(
  context: LoginSecurityContext
): Promise<SecurityCheckResult> {
  const flags: string[] = [];
  let riskScore = 0;
  let blockedReason: string | undefined;

  if (context.honeypotTriggered) {
    await logSecurityEvent('honeypot_triggered', context.ipAddress, context.userAgent, '/login', {
      email: context.email,
    });

    return {
      allowed: false,
      requiresCaptcha: false,
      requiresDelay: false,
      delaySeconds: 0,
      blockedReason: 'Suspicious activity detected',
      riskScore: 100,
      flags: ['honeypot'],
    };
  }

  if (isBlockedUserAgent(context.userAgent)) {
    flags.push('blocked_user_agent');
    riskScore += 60;

    await logSecurityEvent(
      'blocked_user_agent',
      context.ipAddress,
      context.userAgent,
      '/login',
      { email: context.email },
      riskScore
    );

    return {
      allowed: false,
      requiresCaptcha: false,
      requiresDelay: false,
      delaySeconds: 0,
      blockedReason: 'Access denied',
      riskScore,
      flags,
    };
  }

  const uaRiskScore = getUserAgentRiskScore(context.userAgent);
  if (uaRiskScore > 30) {
    riskScore += Math.floor(uaRiskScore / 3);
    flags.push('suspicious_user_agent');
  }

  const emailAnalysis = analyzeInput(context.email);
  if (emailAnalysis.isAnomaly) {
    riskScore += emailAnalysis.riskScore;
    flags.push(...emailAnalysis.detectedPatterns);

    await logSecurityEvent(
      'anomaly_detected',
      context.ipAddress,
      context.userAgent,
      '/login',
      {
        email: emailAnalysis.sanitizedInput,
        patterns: emailAnalysis.detectedPatterns,
      },
      emailAnalysis.riskScore
    );

    if (emailAnalysis.riskScore >= 50) {
      return {
        allowed: false,
        requiresCaptcha: false,
        requiresDelay: false,
        delaySeconds: 0,
        blockedReason: 'Invalid input detected',
        riskScore,
        flags,
      };
    }
  }

  const ipRateLimit = await checkRateLimit(context.ipAddress, 'ip');
  const emailRateLimit = await checkRateLimit(context.email, 'email');

  if (ipRateLimit.blocked || emailRateLimit.blocked) {
    blockedReason =
      ipRateLimit.reason === 'ip_blocked'
        ? 'Too many failed attempts. Please try again later.'
        : 'Account temporarily locked due to multiple failed attempts.';

    return {
      allowed: false,
      requiresCaptcha: false,
      requiresDelay: true,
      delaySeconds: Math.max(ipRateLimit.retryAfter || 0, emailRateLimit.retryAfter || 0),
      blockedReason,
      riskScore: 80,
      flags: ['rate_limited'],
    };
  }

  const maxDelay = Math.max(ipRateLimit.delaySeconds || 0, emailRateLimit.delaySeconds || 0);
  const attemptCount = Math.max(ipRateLimit.attemptCount || 0, emailRateLimit.attemptCount || 0);

  const requiresCaptcha = attemptCount >= 3 && !context.captchaVerified;

  if (requiresCaptcha) {
    flags.push('captcha_required');
  }

  if (!hasValidVerification() && riskScore > 30) {
    const browserResult = verifyBrowser();
    if (!browserResult.isVerified) {
      flags.push('browser_verification_failed');
      riskScore += 20;
    }
  }

  return {
    allowed: !requiresCaptcha && maxDelay === 0,
    requiresCaptcha,
    requiresDelay: maxDelay > 0,
    delaySeconds: maxDelay,
    riskScore,
    flags,
  };
}

export async function recordLoginAttempt(
  context: LoginSecurityContext,
  success: boolean
): Promise<void> {
  if (success) {
    await resetRateLimit(context.ipAddress, 'ip');
    await resetRateLimit(context.email, 'email');

    await logSecurityEvent(
      'login_success',
      context.ipAddress,
      context.userAgent,
      '/login',
      { email: context.email },
      0
    );
  } else {
    await logSecurityEvent(
      'login_failed',
      context.ipAddress,
      context.userAgent,
      '/login',
      { email: context.email },
      20
    );
  }
}

export async function logSecurityEvent(
  eventType: string,
  ipAddress?: string,
  userAgent?: string,
  requestPath?: string,
  metadata?: Record<string, unknown>,
  riskScore: number = 0
): Promise<void> {
  try {
    await supabase.rpc('record_security_event', {
      p_event_type: eventType,
      p_ip_address: ipAddress || null,
      p_user_agent: userAgent || null,
      p_request_path: requestPath || null,
      p_payload_snippet: metadata ? JSON.stringify(metadata).slice(0, 500) : null,
      p_risk_score: riskScore,
      p_metadata: metadata || {},
    });
  } catch (err) {
    console.error('Failed to log security event:', err);
  }
}

export async function getRecentSecurityEvents(
  limit: number = 50
): Promise<unknown[]> {
  try {
    const { data, error } = await supabase
      .from('security_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Failed to fetch security events:', err);
    return [];
  }
}

export function initializeSecurity(): void {
  startMouseTracking();
}

export { getClientIP, analyzeInput, analyzePath, getInputRiskLevel };
